import { memo } from "react";
import { useLocation, useParams } from "react-router";
import {
  useReportListActions,
  useReportListSearchFilter,
  useReportListShouldClearInput,
  useReportListLayout,
  useReportListReportType,
  useReportListOrder,
  useReportListColumnOrder,
  useReportListSearchHistory,
  useReportListSearchScope,
  useReportListFavoritesOnly,
} from "~/store/reportListStore";
import { useReportCRUD } from "~/hooks/useReportCRUD";
import { SearchInput } from "~/components/SearchInput";
import { useSearchInput } from "~/hooks/useSearchInput";
import { SortToggleGroup } from "~/components/SortToggleGroup";
import {
  Badge,
  Button,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "snap-design-system";
import { LayoutGrid, List as ListIcon, X, Star } from "lucide-react";
import {
  REPORT_LIST_TYPE_OPTIONS,
  REPORT_LIST_DEFAULT_TYPE,
  SEARCH_SCOPE_OPTIONS,
  SEARCH_SCOPE_DEFAULT,
} from "~/helpers/constants";
import { countActiveFilters } from "~/helpers/filters.helper";
import { cn } from "~/lib/utils";

function Toolbar() {
  const { folderId } = useParams<{ folderId?: string }>();
  const {
    setSearchFilter,
    clearSearchFilter,
    resetShouldClearInput,
    setLayout,
    setReportType,
    setOrder,
    setColumnOrder,
    setSearchScope,
    clearFilters,
    setFavoritesOnly,
    addSearchHistory,
    removeSearchHistory,
    clearSearchHistory,
  } = useReportListActions();
  const { invalidateToInitialPage } = useReportCRUD(folderId || null);
  const searchFilter = useReportListSearchFilter();
  const shouldClearInput = useReportListShouldClearInput();
  const layout = useReportListLayout();
  const reportType = useReportListReportType();
  const order = useReportListOrder();
  const columnOrder = useReportListColumnOrder();
  const searchHistory = useReportListSearchHistory();
  const searchScope = useReportListSearchScope();
  const favoritesOnly = useReportListFavoritesOnly();
  const location = useLocation();

  const hasActiveFilters =
    reportType !== REPORT_LIST_DEFAULT_TYPE ||
    searchFilter !== "" ||
    searchScope !== SEARCH_SCOPE_DEFAULT ||
    favoritesOnly;

  const filterCount = countActiveFilters({
    searchFilter,
    reportType,
    searchScope,
    favoritesOnly,
  });

  const {
    inputValue,
    inputRef,
    handleChange,
    handleKeyDown,
    clearInput,
    handleSearch,
  } = useSearchInput({
    searchFilter,
    shouldClearInput,
    setSearchFilter,
    clearSearchFilter,
    resetShouldClearInput,
    onSearch: async (value: string, shouldInvalidate = true) => {
      if (value?.length >= 3) {
        addSearchHistory(value);
      }
      if (shouldInvalidate) {
        await invalidateToInitialPage();
      }
    },
    onClear: async () => {
      setSearchScope(SEARCH_SCOPE_DEFAULT);
      await invalidateToInitialPage();
    },
    minSearchLength: 3,
    placeholder: "Procurar nos relatórios",
    requireAuth: true,
  });

  const handleReportTypeChange = async (value: string) => {
    setReportType(value);
    await invalidateToInitialPage();
  };

  const handleSearchScopeChange = async (value: string) => {
    setSearchScope(value);
    // When scope is "folders", report type filter is irrelevant — reset it
    if (value === "folders" && reportType !== REPORT_LIST_DEFAULT_TYPE) {
      setReportType(REPORT_LIST_DEFAULT_TYPE);
    }
    // Scope narrows results on the backend when there is a search term OR when the
    // favorites filter is active, so refetch in either case.
    if (searchFilter || favoritesOnly) {
      await invalidateToInitialPage();
    }
  };

  const handleColumnOrderToggle = async () => {
    setColumnOrder(columnOrder === "modified_at" ? "created_at" : "modified_at");
    await invalidateToInitialPage();
  };

  const handleOrderToggle = async () => {
    setOrder(order === "desc" ? "asc" : "desc");
    await invalidateToInitialPage();
  };

  const handleFavoritesToggle = async () => {
    setFavoritesOnly(!favoritesOnly);
    await invalidateToInitialPage();
  };

  const handleClearFilters = async () => {
    clearFilters();
    clearSearchFilter(true);
    await invalidateToInitialPage();
  };

  const handleToggleLayout = () => {
    setLayout(layout === "masonry" ? "column" : "masonry");
  };



  const isFoldersScope = searchScope === "folders";

  const renderContent = () => {
    if (location.pathname !== "/" && !location.pathname.startsWith("/pasta/")) {
      return null;
    }

    return (
      <div className="flex flex-col xl:flex-row xl:items-center flex-wrap gap-3 w-full min-w-0 max-w-full">
        {/* Search input — full width on small, auto on xl+ */}
        <div className="w-full min-w-0 max-w-full xl:flex-1 xl:min-w-[300px] xl:max-w-[40%]" id="search-input" data-test-id="snap-reports-search-field">
          <SearchInput
            ref={inputRef}
            value={inputValue}
            placeholder="Digite e pressione Enter para buscar"
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClear={clearInput}
            startContent={
              <Select
                value={searchScope}
                onValueChange={handleSearchScopeChange}
              >
                <SelectTrigger
                  className="w-[30%] min-w-[130px] rounded-none border-0 border-r border-input h-full bg-transparent cursor-pointer group-focus-within/search:border-accent-500 group-data-has-value/search:border-accent-500"
                  aria-label="Filtrar escopo de busca"
                  data-test-id="snap-reports-search-scope-select"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SEARCH_SCOPE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            }
            data-test-id="snap-reports-search-input"
          />
        </div>

        <Button
          variant={"ghost"}
          onClick={handleFavoritesToggle}
          aria-pressed={favoritesOnly}
          data-test-id="snap-reports-filter-favorites-toggle"
          title={favoritesOnly ? "Mostrar todos os itens" : "Mostrar apenas favoritos"}
          className={cn(
            "whitespace-nowrap cursor-pointer border border-input min-h-[44px] min-w-[44px]",
            favoritesOnly && "text-accent-500",
          )}
        >
          <Star
            size={16}
            className={favoritesOnly ? "fill-amber-400 text-amber-500" : "fill-none"}
          />
          Favoritos
        </Button>

        {/* Filters + sort/layout — wraps on small, inline on xl+ */}
        <div className="flex flex-wrap items-center justify-between gap-2 w-full min-w-0 max-w-full xl:flex-1">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <Select
              value={reportType}
              onValueChange={handleReportTypeChange}
              disabled={isFoldersScope}
            >
              <SelectTrigger
                variant="dark-rounded"
                className="w-[180px] max-w-full min-h-[44px] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                data-test-id="snap-reports-filter-type-select"
                aria-label="Filtrar por tipo de relatório"
                disabled={isFoldersScope}
              >
                <SelectValue placeholder="Selecionar tipo" />
              </SelectTrigger>
              <SelectContent>
                {REPORT_LIST_TYPE_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={handleClearFilters}
                title="Limpar todos os filtros aplicados"
                className="text-accent-500 hover:text-accent-500 flex items-center gap-1 whitespace-nowrap min-h-[44px] animate-in fade-in slide-in-from-left-2 zoom-in-95 duration-200 ease-out"
                data-test-id="snap-reports-filter-clear-all-button"
              >
                <X size={16} />
                Remover Filtros
                {filterCount > 0 && (
                  <Badge
                    variant="accent"
                    size="small"
                    className="rounded-full"
                    data-test-id="snap-reports-filter-count-badge"
                  >
                    {filterCount}
                  </Badge>
                )}
              </Button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <SortToggleGroup
              columnOrder={columnOrder as "created_at" | "modified_at"}
              order={order as "asc" | "desc"}
              onColumnOrderToggle={handleColumnOrderToggle}
              onOrderToggle={handleOrderToggle}
            />
            <Button
              variant="ghost"
              size="small"
              onClick={handleToggleLayout}
              className="min-h-[44px] min-w-[44px]"
              aria-label={layout === "masonry" ? "Alternar para layout em coluna" : "Alternar para layout em grade"}
              data-test-id="snap-reports-layout-toggle"
            >
              {layout === "masonry" ? <LayoutGrid size={32} /> : <ListIcon size={32} />}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return renderContent();
}

export default memo(Toolbar);
